import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Box, 
  Users, 
  PackageSearch, 
  Settings, 
  Image as ImageIcon, 
  LogOut, 
  ChevronDown, 
  ChevronRight,
  ShoppingCart,
  Database,
  UserCircle,
  Book,
  ClipboardList,
  Store,
  Globe
} from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { usePermission } from '../context/PermissionContext';

const Sidebar = ({ isOpen, setIsOpen, hide }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const { hasAnyResource, hasAnyPermission, hasPermission, isLoading: permLoading } = usePermission();

  useEffect(() => {
    fetch('/api/employees/current')
      .then(r => r.json())
      .then(r => { if (r.status === 'success') setCurrentUser(r.data); })
      .catch(() => {});
  }, []);
  
  const expanded = isOpen || isHovered;

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const menuItems = [
    { 
      name: t('dashboard.name'), 
      icon: <LayoutDashboard size={20} />, 
      path: '/',
      permission: { resources: ['dashboard'], mode: 'resources' }
    },
    { 
      name: t('tasks.title'), 
      icon: <ClipboardList size={20} />, 
      path: '/tasks',
      permission: { resources: ['task'], mode: 'resources' }
    },
    { 
      name: t('sidebar.product_management'), 
      icon: <Box size={20} />, 
      path: '/products',
      permission: { resources: ['product'], mode: 'resources' }
    },
    { 
      name: t('sidebar.category_management'), 
      icon: <PackageSearch size={20} />, 
      path: '/categories',
      permission: { resources: ['category'], mode: 'resources' }
    },
    { 
      name: t('sidebar.order_management'), 
      icon: <ShoppingCart size={20} />, 
      path: '#', // Placeholder
      disabled: true,
      permission: { resources: ['order_placeholder'], mode: 'resources' }
    },
    { 
      name: t('sidebar.inventory_management'), 
      icon: <Database size={20} />, 
      path: '#', // Placeholder
      disabled: true,
      permission: { resources: ['inventory_placeholder'], mode: 'resources' }
    },
    { 
      name: t('sidebar.customer_management'), 
      icon: <UserCircle size={20} />, 
      path: '/customers',
      permission: { resources: ['customer'], mode: 'resources' }
    },
    { 
      name: t('sidebar.channel_management'), 
      icon: <Globe size={20} />, 
      permission: { resources: ['channel_stores', 'channel_products', 'channel_system_attribute_mapping'], mode: 'resources' },
      children: [
        { name: t('channel.store_authorization'), path: '/channel/stores', permission: { resource: 'channel_stores', mode: 'resource' } },
        { name: t('channel.eav_products'), path: '/channel/eav-products', permission: { resource: 'channel_products', mode: 'resource' } },
        { name: t('channel.attribute_mapping'), path: '/channel/attribute-mapping', permission: { resource: 'channel_attributes', mode: 'resource' } },
        { name: '类目同步', path: '/channel/categories', permission: { resource: 'channel_stores', mode: 'resource' } },

        { name: t('settings.system_attr_mapping'), path: '/channel/system-attribute-mapping', permission: { resource: 'channel_system_attribute_mapping', mode: 'resource' } },
      ]
    },
    { 
      name: t('suppliers.title'), 
      icon: <Users size={20} />, 
      path: '/suppliers',
      permission: { resources: ['supplier'], mode: 'resources' }
    },
    { 
      name: t('sidebar.organization_management'), 
      icon: <UserCircle size={20} />, 
      path: '/organization',
      permission: { resources: ['organization', 'org_department', 'org_position'], mode: 'resources' }
    },
    { 
      name: t('media.title'), 
      icon: <ImageIcon size={20} />, 
      path: '/media-library',
      permission: { resources: ['media'], mode: 'resources' }
    },
    { 
      name: t('sidebar.employee_management'), 
      icon: <Users size={20} />, 
      path: '/employees',
      permission: { resources: ['employee'], mode: 'resources' }
    },
    { 
      name: t('help.title'), 
      icon: <Book size={20} />, 
      path: '/help',
      permission: { resources: ['help'], mode: 'resources' }
    },
    { 
      name: t('sidebar.system_settings'), 
      icon: <Settings size={20} />, 
      permission: { resources: ['settings_params', 'settings_attributes', 'settings_attribute_sets', 'settings_category_attribute_rules', 'settings_currency_exchange_rates', 'settings_profit_rates', 'settings_cron'], mode: 'resources' },
      children: [
        { name: t('settings.sys_params'), path: '/settings/params', permission: { resource: 'settings_params', mode: 'resource' } },
        { name: t('settings.attribute_design'), path: '/settings/attributes', permission: { resource: 'settings_attributes', mode: 'resource' } },
        { name: t('settings.attribute_sets'), path: '/settings/attribute-sets', permission: { resource: 'settings_attribute_sets', mode: 'resource' } },
        { name: t('settings.category_attribute_rules'), path: '/settings/category-attribute-rules', permission: { resource: 'settings_category_attribute_rules', mode: 'resource' } },
        { name: t('settings.currency_exchange_rates'), path: '/settings/currency-exchange-rates', permission: { resource: 'settings_currency_exchange_rates', mode: 'resource' } },
        { name: t('settings.profit_rates'), path: '/settings/profit-rates', permission: { resource: 'settings_profit_rates', mode: 'resource' } },
        { name: t('cron.title'), path: '/settings/cron', permission: { resource: 'settings_cron', mode: 'resource' } },
      ]
    },
  ];

  return (
    <div 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        ${hide ? 'hidden' : 'flex'} fixed md:static h-screen md:h-full ${theme === 'light' ? 'bg-white text-slate-800' : 'bg-slate-950 text-slate-400'} flex-col transition-all duration-300 ease-in-out z-40
        ${expanded ? 'w-64 translate-x-0' : 'w-0 md:w-20 -translate-x-full md:translate-x-0'}
        ${expanded ? (theme === 'light' ? 'border-r border-slate-200 shadow-[4px_0_15px_rgba(0,0,0,0.1)]' : 'border-r-2 border-slate-700 shadow-[4px_0_15px_rgba(0,0,0,0.5)]') : theme === 'light' ? 'border-r-0 shadow-none md:border-r md:border-slate-200 md:shadow-[4px_0_15px_rgba(0,0,0,0.1)]' : 'border-r-0 shadow-none md:border-r-2 md:border-slate-700 md:shadow-[4px_0_15px_rgba(0,0,0,0.5)]'}
        ${expanded ? 'pointer-events-auto' : 'pointer-events-none md:pointer-events-auto'}
        ${expanded ? 'opacity-100' : 'opacity-0 md:opacity-100'}
        ${expanded ? 'visible' : 'invisible md:visible'}
        relative
      `}
    >
      <div className={`absolute top-0 left-0 w-full h-full pointer-events-none border-l ${theme === 'light' ? 'border-slate-200/50 shadow-[inset_10px_0_20px rgba(0,0,0,0.1)]' : 'border-slate-700/30 shadow-[inset_10px_0_20px rgba(0,0,0,0.3)]'}`} />

      <div className={`p-6 font-bold text-xl border-b ${theme === 'light' ? 'text-slate-800 border-slate-200' : 'text-white border-slate-800'} flex items-center gap-3 shrink-0 overflow-hidden whitespace-nowrap`}>
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shadow-[0_0_10px_rgba(37,99,235,0.5)] shrink-0">E</div>
        {expanded && <span className="transition-opacity duration-300">EOATO</span>}
      </div>

      <nav className="flex-1 p-4 space-y-2 overflow-y-auto overflow-x-hidden">
        {menuItems.filter(item => {
          // 权限未加载完成时，先显示菜单项（但过滤掉 disabled 的占位项）
          if (permLoading) return !item.disabled;
          // Check permission: if item has permission config, verify access
          if (!item.permission) return true; // No permission check needed
          if (item.permission.mode === 'resources') {
            return hasAnyResource(item.permission.resources);
          }
          if (item.permission.mode === 'resource') {
            return hasPermission(item.permission.resource);
          }
          return true;
        }).map((item, idx) => {
          const hasChildren = item.children && item.children.length > 0;
          // Filter children by permission too
          const visibleChildren = hasChildren
            ? item.children.filter(child => {
                if (!child.permission) return true;
                return hasPermission(child.permission.resource);
              })
            : [];
          const isExpanded = expandedMenus[item.name];

          if (!hasChildren) {
            return (
              <Link 
                key={item.path || idx} 
                to={item.path} 
                className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 group ${item.disabled ? 'opacity-40 cursor-not-allowed' : theme === 'light' ? 'hover:bg-slate-100 hover:text-blue-600' : 'hover:bg-blue-900/30 hover:text-blue-400'}`}
              >
                <div className="shrink-0 group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                {expanded && <span className="transition-opacity duration-300 whitespace-nowrap">{item.name}</span>}
              </Link>
            );
          }

          return (
            <div key={idx} className="space-y-1">
              <button 
                onClick={() => toggleMenu(item.name)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 group ${theme === 'light' ? 'hover:bg-slate-100 hover:text-blue-600' : 'hover:bg-blue-900/30 hover:text-blue-400'}`}
              >
                <div className="shrink-0 group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                {expanded && (
                  <div className="flex flex-1 items-center justify-between whitespace-nowrap">
                    <span>{item.name}</span>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </div>
                )}
              </button>
              
              {expanded && isExpanded && visibleChildren.length > 0 && (
                <div className={`ml-9 space-y-1 border-l ${theme === 'light' ? 'border-slate-200' : 'border-slate-800'} pl-2`}>
                  {visibleChildren.map((child, cIdx) => (
                    <Link 
                      key={cIdx} 
                      to={child.path} 
                      className={`block p-2 text-sm rounded-md transition-colors whitespace-nowrap ${theme === 'light' ? 'hover:bg-slate-100 hover:text-blue-600' : 'hover:bg-blue-900/20 hover:text-blue-400'}`}
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className={`p-4 border-t ${theme === 'light' ? 'border-slate-200 bg-slate-100/50' : 'border-slate-800 bg-slate-900/50'} space-y-4`}>
        <Link 
          to="/profile" 
          className={`flex items-center gap-3 p-2 rounded-lg transition-all duration-300 ${theme === 'light' ? 'hover:bg-slate-200' : 'hover:bg-slate-800'}`}
        >
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shrink-0 text-white font-semibold text-sm">
            {currentUser?.email ? currentUser.email.charAt(0).toUpperCase() : '?'}
          </div>
          {expanded && (
            <div className="flex flex-col overflow-hidden">
              <span className={`text-xs truncate ${theme === 'light' ? 'text-slate-600' : 'text-slate-500'}`}>{currentUser?.email || '...'}</span>
            </div>
          )}
        </Link>

        <button 
          onClick={handleLogout}
          className={`flex items-center gap-3 p-3 w-full rounded-lg transition-all duration-200 group ${theme === 'light' ? 'hover:bg-red-100 hover:text-red-600' : 'hover:bg-red-900/30 hover:text-red-400'}`}
        >
          <div className="shrink-0 group-hover:scale-110 transition-transform">
            <LogOut size={20} />
          </div>
          {expanded && <span className="transition-opacity duration-300 whitespace-nowrap">{t('auth.logout')}</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
